import { useAuthStore } from '@/context/authStore';
import { useAdminStats, useAdminAlerts, useAdminProfitHistory } from '@/hooks/useAdmin';
import {
  Building2,
  TrendingUp,
  TrendingDown,
  Wallet,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
} from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuthStore();
  const { data: stats, isLoading: loadingStats } = useAdminStats();
  const { data: alerts } = useAdminAlerts();
  const { data: profitHistory } = useAdminProfitHistory(6);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-indigo-900 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4"></div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-slate-400 text-sm">Panel de Administración</span>
          </div>
          <h1 className="text-3xl font-bold">
            {getGreeting()}, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-slate-400 mt-1">
            Gestiona tu negocio SaaS desde un solo lugar
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {loadingStats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 animate-pulse">
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Clientes Activos */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">{stats?.active_tenants || 0}</p>
                <p className="text-sm text-gray-500">Clientes Activos</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Total clientes</span>
                <span className="font-medium text-gray-900">{stats?.total_tenants || 0}</span>
              </div>
            </div>
          </div>

          {/* Ingresos Mensuales (MRR) */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(stats?.monthly_revenue || 0)}
                </p>
                <p className="text-sm text-gray-500">Ingresos Mensuales</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-emerald-600">
                <TrendingUp className="h-4 w-4" />
                <span>MRR (Monthly Recurring Revenue)</span>
              </div>
            </div>
          </div>

          {/* Gastos Mensuales */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Wallet className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(stats?.monthly_expenses || 0)}
                </p>
                <p className="text-sm text-gray-500">Gastos Mensuales</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <TrendingDown className="h-4 w-4" />
                <span>Hosting, APIs, Dominio</span>
              </div>
            </div>
          </div>

          {/* Ganancia Neta */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
                (stats?.net_profit || 0) >= 0
                  ? 'bg-gradient-to-br from-green-500 to-green-600 shadow-green-500/20'
                  : 'bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/20'
              }`}>
                <DollarSign className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className={`text-3xl font-bold ${
                  (stats?.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(stats?.net_profit || 0)}
                </p>
                <p className="text-sm text-gray-500">Ganancia Neta</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Margen</span>
                <span className={`font-medium ${
                  (stats?.profit_margin || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stats?.profit_margin?.toFixed(1) || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Two columns: Profit History + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit History */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Evolución de Rentabilidad</h2>
            <p className="text-sm text-gray-500">Últimos 6 meses</p>
          </div>
          <div className="p-6">
            {profitHistory?.data && profitHistory.data.length > 0 ? (
              <div className="space-y-4">
                {profitHistory.data.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 w-20">{item.month}</span>
                    <div className="flex-1 mx-4 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          item.profit >= 0 ? 'bg-green-500' : 'bg-red-500'
                        }`}
                        style={{
                          width: `${Math.min(Math.abs(item.margin), 100)}%`,
                        }}
                      />
                    </div>
                    <span className={`text-sm font-medium w-24 text-right ${
                      item.profit >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(item.profit)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>No hay datos de rentabilidad</p>
              </div>
            )}
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">Alertas</h2>
              <p className="text-sm text-gray-500">Acciones pendientes</p>
            </div>
            {alerts && alerts.total > 0 && (
              <span className="px-2.5 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                {alerts.total}
              </span>
            )}
          </div>
          <div className="p-6">
            {alerts?.alerts && alerts.alerts.length > 0 ? (
              <div className="space-y-3">
                {alerts.alerts.slice(0, 5).map((alert, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg ${
                      alert.severity === 'error'
                        ? 'bg-red-50'
                        : alert.severity === 'warning'
                        ? 'bg-amber-50'
                        : 'bg-blue-50'
                    }`}
                  >
                    {alert.severity === 'error' ? (
                      <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    ) : alert.severity === 'warning' ? (
                      <Clock className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        alert.severity === 'error'
                          ? 'text-red-900'
                          : alert.severity === 'warning'
                          ? 'text-amber-900'
                          : 'text-blue-900'
                      }`}>
                        {alert.message}
                      </p>
                      {alert.tenant_name && (
                        <p className="text-xs text-gray-500 mt-0.5">{alert.tenant_name}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-200" />
                <p>No hay alertas pendientes</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Status Summary */}
      {stats && (stats.pending_payments > 0 || stats.overdue_payments > 0) && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Estado de Pagos</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl">
              <Clock className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.pending_payments}</p>
                <p className="text-sm text-amber-600">Pagos Pendientes</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-red-50 rounded-xl">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.overdue_payments}</p>
                <p className="text-sm text-red-600">Pagos Vencidos</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {stats.active_tenants - stats.pending_payments - stats.overdue_payments}
                </p>
                <p className="text-sm text-green-600">Al Día</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
